import { headerApiService } from "@/src/lib/api-services/headerApiServices";
import { myAccountApiService } from "@/src/lib/api-services/myAccountApiServices";
import { testApiService } from "@/src/lib/api-services/testApiServices";
import { useCustomQuery } from "@/src/lib/hooks/useFetch";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import { useToast } from "@/src/lib/hooks/useToast";
import { httpStatus } from "@/src/lib/util/config";
import { fetchSessionDetails } from "@/src/lib/util/fetchSessionDetails";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

export function useGetPatientDetails(patientId) {
    const {
        data: patientDetails,
        isError: isErrorPatientDetails,
        error: errorPatientDetails,
        refetch: refetchPatientDetails,
        isFetching: isFetchingPatientDetails,
        isLoading: isLoadingPatientDetails,
    } = useCustomQuery(
        ["patient_details", patientId],
        () => myAccountApiService.getPatientDetailsById(patientId),
        {
            enabled: !!patientId,
        }
    );

    if (isErrorPatientDetails) {
        const { status, data } = errorPatientDetails?.response || {};
        if (status === httpStatus.CONFLICT) {
        } else if (status === httpStatus.BAD_REQUEST) {
        } else {
            throw errorPatientDetails;
        }
    }

    return {
        patientDetails: patientDetails?.data || [],
        isErrorPatientDetails,
        errorPatientDetails,
        refetchPatientDetails,
        isFetchingPatientDetails,
        isLoadingPatientDetails,
    };
}

export function useUpdatePatientDetails() {
    const { showCustomToast } = useToast();
    const {
        isError: isErrorUpdatePatientDetails,
        error: errorUpdatePatientDetails,
        isSuccess: isSuccessUpdatePatientDetails,
        data: updatePatientDetailsResponse,
        reset: resetUpdatePatientDetails,
        mutate: mutateUpdatePatientDetails,
        isLoading: isLoadingUpdatePatientDetails,
    } = useHODMutation(myAccountApiService.updatePatientDetails);

    if (isSuccessUpdatePatientDetails) {
        const { success, message } = updatePatientDetailsResponse || {};
        if (success) {
            showCustomToast(message, "success");
            resetUpdatePatientDetails();
        }
    }
    useEffect(() => {
        if (isErrorUpdatePatientDetails) {
            const { status, data } = errorUpdatePatientDetails?.response || {};
            if (status === httpStatus.BAD_REQUEST) {
                showCustomToast(
                    data?.message || errorUpdatePatientDetails?.message,
                    {
                        variant: "error",
                    }
                );
            }
        }
    }, [isErrorUpdatePatientDetails]);
    return {
        mutateUpdatePatientDetails,
        isLoadingUpdatePatientDetails,
        isSuccessUpdatePatientDetails,
    };
}

export function useCreatePatient({
    noRedirect = false,
    handleClose,
    refetchPatientList,
} = {}) {
    const dispatch = useDispatch();
    const { showCustomToast } = useToast();
    const router = useRouter();
    const {
        isError: isErrorCreatePatient,
        error: errorCreatePatient,
        isSuccess: isSuccessCreatePatient,
        data: createPatientResponse,
        reset: resetCreatePatient,
        mutate: mutateCreatePatient,
        isLoading: isLoadingCreatePatient,
    } = useHODMutation(testApiService.createPatient);

    useEffect(() => {
        if (isSuccessCreatePatient) {
            const { success, message } = createPatientResponse?.data || {};
            if (success) {
                showCustomToast(message, "success");
                resetCreatePatient();
                fetchSessionDetails(dispatch);
                if (noRedirect) {
                    handleClose?.();
                    refetchPatientList?.();
                } else {
                    router.push("/my-account/members");
                }
            }
        }
    }, [isSuccessCreatePatient, dispatch, router, noRedirect]);

    useEffect(() => {
        if (isErrorCreatePatient) {
            const { status, data } = errorCreatePatient?.response || {};
            if (status === httpStatus.BAD_REQUEST) {
                showCustomToast(data?.message || errorCreatePatient?.message, {
                    variant: "error",
                });
            }
        }
    }, [isErrorCreatePatient]);
    return {
        mutateCreatePatient,
        isLoadingCreatePatient,
        isSuccessCreatePatient,
    };
}

export function useSignUp() {
    const { showCustomToast } = useToast();
    const router = useRouter();
    const {
        isError: isErrorSignUp,
        error: errorSignUp,
        isSuccess: isSuccessSignUp,
        data: SignUpResponse,
        reset: resetSignUp,
        mutate: mutateSignUp,
        isLoading: isLoadingSignUp,
    } = useHODMutation(headerApiService.signUp);

    if (isSuccessSignUp) {
        const { success } = SignUpResponse || {};
        if (success) {
            resetSignUp();
            router.push("/");
        }
    }

    useEffect(() => {
        if (isErrorSignUp) {
            const { status, data } = errorSignUp?.response || {};
            if (status === httpStatus.BAD_REQUEST) {
                showCustomToast(data?.message || errorSignUp?.message, {
                    variant: "error",
                });
            } else {
                throw errorSignUp;
            }
        }
    }, [isErrorSignUp]);
    return {
        mutateSignUp,
        isLoadingSignUp,
        isSuccessSignUp,
        SignUpResponse,
    };
}
